
/**
 * MainProduct
 * --------------------------------------------------------------------
 * One Custom Element that owns all JS behaviours for the product page
 * (sections/main-product.liquid). Each interactive block inside the
 * product page is its own nested Custom Element so logic stays
 * isolated and re-usable.
 *
 * Usage in Liquid:
 *   <main-product>
 *     ... product markup (pop triggers, tabs, etc.) ...
 *   </main-product>
 *   <script src="{{ 'main-product.js' | asset_url }}" defer></script>
 *
 * Conventions (match other components in this theme, e.g. cart-coupons.js,
 * modal.js, details-modal.js):
 *   - One class per behaviour
 *   - All lookups are scoped to `this` (the host element) so multiple
 *     instances on a page never collide
 *   - State lives on the instance; global side-effects are kept to a
 *     minimum (only the `popshow` body class for scroll-lock, which
 *     toggles based on whether ANY instance has a pop open)
 * --------------------------------------------------------------------
 */

if (!customElements.get('main-product')) {
  class CollapsibleContentPop {
    constructor(container) {
      this.container = container
      this.pop = container.querySelector('.collapsible-content-new-pop')
      this.panel = this.pop?.querySelector('.collapsible-content-new-pop-container')
      this.trigger = container.querySelector('.collapsible-content-new-wrapper')
      this.closeBtn = this.pop?.querySelector('.close_collapsible_pop')
      if (!this.pop || !this.panel || !this.trigger) return

      this._onTrigger = this._onTrigger.bind(this)
      this._onPopClick = this._onPopClick.bind(this)
      this._onKey = this._onKey.bind(this)

      this.trigger.addEventListener('click', this._onTrigger)
      // Listen on the pop element (overlay + panel) so we can detect
      // overlay clicks via `e.target === this.pop`. Panel clicks are
      // inside the panel, so they won't match.
      this.pop.addEventListener('click', this._onPopClick)
      document.addEventListener('keydown', this._onKey)
    }

    destroy () {
      this.trigger?.removeEventListener('click', this._onTrigger)
      this.pop?.removeEventListener('click', this._onPopClick)
      document.removeEventListener('keydown', this._onKey)
    }

    open () {
      this._populate()
      this.pop.style.display = 'block'
      // Force a reflow so the `transform` transition actually runs.
      void this.pop.offsetHeight
      this.panel.style.transform = 'translateY(0)'
      document.body.classList.add('popshow')
      document.documentElement.classList.add('popshow')
    }

    close () {
      this.panel.style.transform = 'translateY(100%)'
      setTimeout(() => {
        this.pop.style.display = 'none'
        this._clear()
        // Only unlock body scroll if no other pop is open.
        if (!document.querySelector('.collapsible-content-new-pop[style*="block"]')) {
          document.body.classList.remove('popshow')
          document.documentElement.classList.remove('popshow')
        }
      }, 400)
    }

    _onTrigger (e) {
      e.preventDefault()
      this.open()
    }

    _onPopClick (e) {
      // e.target === this.pop means the user clicked the overlay,
      // not anything inside the panel.
      if (e.target === this.pop || e.target.closest('.close_collapsible_pop')) {
        this.close()
      }
    }

    _onKey (e) {
      if (e.key === 'Escape' && this.pop.style.display === 'block') {
        this.close()
      }
    }

    /**
     * Pull the right content into the pop. There are two flavours:
     *   - Default: copy every `.disclosure__content_new` block from the
     *     desktop (>= lg) markup.
     *   - Shipping: copy the single shipping disclosure's content.
     */
    _populate () {
      if (this.trigger.classList.contains('collapsible-content-shipping')) {
        const src = document.querySelector('.collapsible-shipping_wrapper .disclosure__content')
        const dest = this.pop.querySelector('.collapsible-content-new-pop-content')
        if (src && dest) dest.innerHTML = src.innerHTML
        return
      }
      const wrapper = this.pop.querySelector('.collapsible-content-new-pop-wrapper')
      if (!wrapper) return
      wrapper.innerHTML = [...document.querySelectorAll('.disclosure__content_new')]
        .map(d => `<div class="collapsible-content-new-pop-content">${d.innerHTML}</div>`)
        .join('')
    }

    _clear () {
      this.pop
        .querySelectorAll('.collapsible-content-new-pop-wrapper, .collapsible-content-new-pop-content')
        .forEach(el => { el.innerHTML = '' })
    }
  }
  customElements.define('main-product', class MainProduct extends HTMLElement {
    constructor() {
      super()
      this._popInstances = new Set()
    }

    connectedCallback () {
      // ----- Collapsible-content pop (Product Details / Shipping) -----
      this.querySelectorAll('.collapsible-content-new-container').forEach(el => {
        this._popInstances.add(new CollapsibleContentPop(el))
      })
    }

    disconnectedCallback () {
      this._popInstances.forEach(p => p.destroy())
      this._popInstances.clear()
    }
  })
}